<script setup lang="ts">
import { ref, defineExpose, computed, nextTick } from 'vue';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  getHighestZindex,
} from '@inscribe/signal';
import { OnboardingService } from '@/shared/services/onboarding/onboarding.service';
import { useOnboardingStore } from '@/V2/stores/pinia/onboarding.store';
import { OnboardingFlow } from '@/types/onboarding.types';

/** Refs */
const isOpen = ref<boolean>(false);
const store = useOnboardingStore();
const onboardingFlow = computed(() => store.getActiveOnboardingFlow);
const zIndex = getHighestZindex();
const publicPath = getPublicPath();

/** Emits */
const emit = defineEmits<(e: 'close') => void>();

/** Handlers */
function openOnboardingLaunchScreen() {
  isOpen.value = true;
}

function closeOnboardingLaunchScreen() {
  isOpen.value = false;
  emit('close');
}

function skipTour() {
  closeOnboardingLaunchScreen();
  OnboardingService.skipOnboardingFlow(onboardingFlow.value as OnboardingFlow);
}

function showNextStep() {
  closeOnboardingLaunchScreen();
  nextTick(() => {
    OnboardingService.showNextStep();
  });
}

/** Helpers */
function getPublicPath() {
  const currentUrl = window.location.href;
  const parsedUrl = new URL(currentUrl);
  return parsedUrl.protocol + '//' + parsedUrl.host + parsedUrl.pathname;
}

/** Expose */
defineExpose({
  skipTour,
  openOnboardingLaunchScreen,
  closeOnboardingLaunchScreen,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
});
</script>

<template>
  <div
    v-if="isOpen && onboardingFlow?.launchScreenProps"
    ref="promptBoxEl"
    class="onboarding-launch-screen"
    :style="{ zIndex: zIndex }"
  >
    <Transition name="fade" appear>
      <div
        class="modal d-flex flex-column align-items-center justify-content-between"
        @click.stop
      >
        <div class="content i-p-10 i-pb-11 d-flex flex-column i-gap-8">
          <!-- image -->
          <div class="img-container i-radius-3xl">
            <img
              :src="`${publicPath}${onboardingFlow.launchScreenProps?.imageUrl}`"
              :alt="onboardingFlow.launchScreenProps?.title"
            />
          </div>

          <!-- content -->
          <div class="text-container d-flex flex-column i-gap-8">
            <div class="title i-text-heading-3">
              {{ onboardingFlow.launchScreenProps?.title }}
            </div>
            <!-- component -->
            <component
              :is="onboardingFlow.launchScreenProps?.contentComponent"
            />
          </div>
        </div>

        <!-- footer -->
        <div
          class="footer d-flex flex-row justify-content-space-between align-items-center i-p-8"
        >
          <i-button
            :button-style="iButtonStyles.GHOST"
            :size="iButtonSizes.MEDIUM"
            :type="iButtonTypes.NEUTRAL"
            label="Skip Tour"
            @button-click="skipTour"
          />
          <i-button
            :button-style="iButtonStyles.SOLID_DARK"
            :size="iButtonSizes.MEDIUM"
            :type="iButtonTypes.PRIMARY"
            label="Continue"
            right-icon="medium-chevron-right"
            @button-click="showNextStep"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.onboarding-launch-screen {
  background-color: rgba($light-neutral-120, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .modal {
    $border-radius: 32px;

    position: absolute;
    width: 512px;
    height: 626px;
    top: 50%;
    left: 50%;
    background-color: var(--base);
    border-radius: $border-radius;
    transform: translate(-50%, -50%);

    .img-container {
      height: 268px;
      overflow: hidden;
      background: var(--fill-neutral-subtle);

      img {
        width: 100%;
        height: 100%;
      }
    }

    .footer {
      background: var(--fill-neutral-subtle);
      width: 100%;
      border-bottom-left-radius: $border-radius;
      border-bottom-right-radius: $border-radius;
    }
  }
}
</style>
